import { useEffect, useMemo, useState } from "react";
import { Box, TextField, Alert } from "@mui/material";
import { FormContainer } from "./Profile.styles";
import { changePasswordThunk } from "../../store/thunk/profileThunk";
import { useAppDispatch } from "../../hooks/StoreHooks";
import { resetPasswordChangeStatus } from "../../store/reducers/profileReducer";
import PasswordStrength, { passwordStrengthTest } from "../../authentication/ui/PasswordStrength";
import PasswordRules from "../../authentication/ui/PasswordRules";
import { useTranslation } from "react-i18next";
import { GeneralButton } from "../../common/ui/GeneralButton";

interface Props {
    userId: string;
}

export function PasswordChange({ userId }: Props) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [repeatNewPassword, setRepeatNewPassword] = useState("");
    const [validationError, setValidationError] = useState("");
    const [success, setSuccess] = useState("");
    const [apiError, setApiError] = useState("");

    const passwordStrength = useMemo(() => passwordStrengthTest(newPassword), [newPassword]);

    const isPasswordValid = useMemo(() => {
        return (
            newPassword.length >= 8 &&
            /[A-Z]/.test(newPassword) &&
            /[a-z]/.test(newPassword) &&
            /[0-9]/.test(newPassword) &&
            /[!-\/:-@[-`{-~]/.test(newPassword) &&
            newPassword === repeatNewPassword
        );
    }, [newPassword, repeatNewPassword]);

    useEffect(() => {
        if (success) {
            setOldPassword("");
            setNewPassword("");
            setRepeatNewPassword("");
        }

        return () => {
            dispatch(resetPasswordChangeStatus());
        };
    }, [success, dispatch]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setValidationError("");
        setApiError("");
        setSuccess("");

        if (!isPasswordValid) {
            setValidationError(t("profilePage.passwordDoesNotMeetRequirements"));
            return;
        }

        const result = await dispatch(
            changePasswordThunk({ userId, currentPassword: oldPassword, newPassword })
        ).unwrap();

        if (result.success()) {
            setSuccess(t("profilePage.passwordUpdatedSuccessfully"));
        } else {
            try {
                const errorBody = JSON.parse(result.errorMessage || "{}");
                setApiError(errorBody[0].description || t("profilePage.failedToChangePassword"));
            } catch {
                setApiError(t("profilePage.failedToChangePassword"));
            }
        }
    };

    return (
        <FormContainer onSubmit={handleSubmit}>
            {validationError && <Alert severity="error">{validationError}</Alert>}
            {apiError && <Alert severity="error">{apiError}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <TextField
                type="password"
                label={t("profilePage.oldPassword")}
                fullWidth
                required
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
            />
            <TextField
                type="password"
                label={t("profilePage.newPassword")}
                fullWidth
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
            />
            <PasswordStrength strength={passwordStrength} />
            <PasswordRules pw={newPassword} />
            <TextField
                type="password"
                label={t("profilePage.repeatNewPassword")}
                fullWidth
                required
                value={repeatNewPassword}
                onChange={(e) => setRepeatNewPassword(e.target.value)}
                error={newPassword !== repeatNewPassword}
                helperText={
                    newPassword !== repeatNewPassword && repeatNewPassword.length > 0
                        ? t("profilePage.passwordsDoNotMatch")
                        : ""
                }
            />
            <Box mt={2}>
                <GeneralButton type="submit" disabled={!isPasswordValid}>
                    {t("profilePage.updatePassword")}
                </GeneralButton>

            </Box>
        </FormContainer>
    );
}
